import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';

import {
  fetchBrandReportingUnits,
  fetchReportingUnitGroups,
} from '../../redux/actions/reportingUnitsActions';
import {
  getReportingUnitById,
  getReportingUnitsByBrand,
} from '../../redux/selectors/reportingUnitsSelectors';

import { RBIChipsInput } from '../RBIChipsInput';
import RBIAutocomplete from '../RBIAutocomplete/RBIAutocomplete';
import { TreeSelector } from './../../components/TreeSelector';
import { useTranslation } from 'react-i18next';

export function ParseRequestAvailabilities(items: any[] = []) {
  return items.map(item => item.metas.groupId);
}

export function GetValuesFromPayload(brand: string, values: any[] = []) {
  const parsedValue = values.map(value => ({
    text: value.franchise_name,
    path: `/${brand}/${value.reporting_unit_id}/${value.franchise_group_id}`,
    metas: {
      groupId: value.franchise_group_id,
      groupName: value.franchise_name,
      reportUnitId: value.reporting_unit_id,
      reportUnitName: value.reporting_unit_name,
    },
  }));
  return parsedValue;
}

export function EquipmentFranchiseGroupsSelector({
  id,
  brand,
  label,
  value = [],
  disabled = false,
  onChange = (arg: any[]) => {},
}: any) {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const elFilter = useRef<any>();
  const [selectedReportingUnit, setSelectedReportingUnit] = useState<any>({});
  const reportingUnits = useSelector(getReportingUnitsByBrand(brand));
  const reportingUnit = useSelector(
    getReportingUnitById(brand, selectedReportingUnit?.id),
  );

  const groupsNodes = useMemo(() => {
    let nodes;
    if (reportingUnit) {
      if (reportingUnit.groups) {
        nodes = reportingUnit.groups.map(group => ({
          text: group.name,
          path: `/${brand}/${reportingUnit.id}/${group.id}`,
          hasChildren: false,
          metas: {
            groupId: group.id,
            groupName: group.name,
            reportUnitId: reportingUnit.id,
            reportUnitName: reportingUnit.name,
          },
        }));
      } else {
        nodes = [];
      }
    }
    return nodes;
  }, [reportingUnit?.groups]);

  useEffect(() => {
    if (!reportingUnits) dispatch(fetchBrandReportingUnits(brand));
  }, [brand]);

  const handleChange = (values: any[]) => {
    onChange(values);
  };

  const handleChangeAutocomplete = (name: string, selection: any) => {
    if (selection && !selection.groups) {
      dispatch(fetchReportingUnitGroups(brand, selection.id));
    }
    setSelectedReportingUnit(selection || {});
  };
  const handleFilterFocus = () => {
    elFilter.current.focus();
  };

  const clearReportingUnit = () => {
    setSelectedReportingUnit({});
  };

  const getOptionSelected = (option: any, value: any) =>
    option.id === value?.id;
  const getChipText = (item: any) => item.text;

  return (
    <RBIChipsInput
      label={label}
      value={value}
      disabled={disabled}
      onChange={handleChange}
      getChipText={getChipText}
      onExpandedChange={handleFilterFocus}
      onBlur={clearReportingUnit}
      onFocus={clearReportingUnit}
    >
      <Box
        style={{
          width: '100%',
          height: '100%',
        }}
        flexDirection="column"
        justifyContent="start"
      >
        <Box>
          <RBIAutocomplete
            inputRef={elFilter}
            id={id}
            label={t('app.common.filterByReportingUnit')}
            value={selectedReportingUnit}
            options={reportingUnits || []}
            disabled={disabled}
            width="100%"
            style={{
              backgroundColor: 'white',
              padding: '5px 10px',
            }}
            freeSolo={false}
            getOptionSelected={getOptionSelected}
            onChangeInput={handleChangeAutocomplete}
          />
        </Box>
        <Box
          flexGrow={1}
          style={{
            maxHeight: 180,
            overflowY: 'scroll',
          }}
        >
          <TreeSelector
            nodes={groupsNodes}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            showNoDataOnRoot={true}
          />
        </Box>
      </Box>
    </RBIChipsInput>
  );
}
